import React, { useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Message } from 'primereact/message';
import { classNames } from 'primereact/utils';
import { InputNumber } from 'primereact/inputnumber';
import { getAllRecipeTypeDefaults, getRecipeTypeDefaults } from '../recipeTypeDefaults';

/**
 * Core form fields for recipe details, type, description,
 * categories and configuration ratios.
 */
const RecipeFormFields = ({
	formik,
	categories,
	recipeTypeSettings,
	charCountDescription,
	maxCharCountDescription,
}) => {
	const [categoryHelpVisible, setCategoryHelpVisible] = useState(false);

	const handleRecipeTypeChange = (event) => {
		const selectedType = (event.value || '').toUpperCase();
		const settings = recipeTypeSettings.find(
			(setting) => (setting.recipeType || '').toUpperCase() === selectedType,
		);
		const defaults = getRecipeTypeDefaults(formik.values, settings, true);

		formik.setValues({ ...formik.values, recipeType: event.value, ...defaults }, true);
		formik.setFieldTouched('recipeType', true, false);
	};

	const selectedRecipeTypeSettings = recipeTypeSettings.find(
		(setting) =>
			(setting.recipeType || '').toUpperCase() === (formik.values.recipeType || '').toUpperCase(),
	);
	const isWeightRatioZero = formik.values.weightRatio === 0;
	const weightRatioDefaults = recipeTypeSettings
		.filter((setting) => setting.weightRatio != null)
		.map((setting) => `${setting.recipeType}: ${setting.weightRatio}`)
		.join(' • ');

	const applyRecipeTypeDefaults = () => {
		const defaults = getAllRecipeTypeDefaults(selectedRecipeTypeSettings);
		if (Object.keys(defaults).length === 0) {
			return;
		}

		confirmDialog({
			header: 'Apply recipe type defaults?',
			message: `This will overwrite the water ratio, weight ratio, steam time, and blend time with the defaults for ${formik.values.recipeType}. Continue?`,
			icon: 'pi pi-exclamation-triangle',
			acceptLabel: 'Apply defaults',
			rejectLabel: 'Cancel',
			acceptClassName: 'p-button-warning',
			accept: () => {
				formik.setValues({ ...formik.values, ...defaults }, true);
			},
		});
	};

	return (
		<>
			<ConfirmDialog />
			<Dialog
				visible={categoryHelpVisible}
				header="Recipe categories"
				modal
				style={{ width: '34rem' }}
				breakpoints={{ '960px': '75vw', '641px': '90vw' }}
				onHide={() => setCategoryHelpVisible(false)}
			>
				<ul className="m-0 pl-4 line-height-3">
					<li className="mb-3">
						<strong>Original:</strong> Automatically selected for new recipes to identify them as created
						in-house.
					</li>
					<li className="mb-3">
						<strong>Machine:</strong> Required if the recipe needs to be visible on a machine.
					</li>
					<li className="mb-3">
						<strong>Mobile:</strong> Only recipes with this category are shown to users in the mobile app.
					</li>
					<li className="mb-3">
						<strong>Featured:</strong> Adds the recipe to the Featured tab on the machine.
					</li>
					<li>
						<strong>Other categories:</strong> Optional categories make recipes easier to filter in this client
						app. For example, adding <strong>Therme</strong> lets you use the Therme filter when assigning recipes
						to a machine.
					</li>
				</ul>
			</Dialog>

			<div className="p-field p-col-12 p-md-6 mt-5">
				<label htmlFor="recipeName">Recipe name</label>
				<InputText
					id="recipeName"
					name="recipeName"
					placeholder="Enter recipe name"
					type="text"
					onChange={formik.handleChange}
					onBlur={formik.handleBlur}
					value={formik.values.recipeName}
					className={classNames({ 'p-invalid': formik.touched.recipeName && formik.errors.recipeName })}
				/>
				{formik.touched.recipeName && formik.errors.recipeName && (
					<small id="recipeName-help" className="p-error">
						<i className="pi pi-exclamation-triangle mr-1" />
						{formik.errors.recipeName}
					</small>
				)}
			</div>

			<div className="p-field p-col-12 p-md-6 mt-3">
				<label htmlFor="description" className="flex align-items-center justify-content-between">
					Description
					<small
						id="description-help"
						className={`p-info ${charCountDescription >= maxCharCountDescription ? 'p-error' : ''} float-right`}
					>
						{charCountDescription}/{maxCharCountDescription}
					</small>
				</label>
				<InputTextarea
					id="description"
					name="description"
					placeholder="Enter description"
					type="text"
					onChange={(e) => {
						// keep counter in parent
						formik.handleChange(e);
					}}
					onBlur={formik.handleBlur}
					rows={5}
					cols={30}
					autoResize
					value={formik.values.description}
					className={classNames({ 'p-invalid': formik.touched.description && formik.errors.description })}
				/>
				{formik.touched.description && formik.errors.description && (
					<small id="description-error" className="p-error">
						<i className="pi pi-exclamation-triangle mr-1" />
						{formik.errors.description}
					</small>
				)}
			</div>

			<div className="p-field p-col-12 p-md-6 mt-3">
				<label htmlFor="recipeType">Recipe Type</label>
				{recipeTypeSettings.length > 0 ? (
					<div className="flex flex-wrap gap-2">
						<Dropdown
							id="recipeType"
							name="recipeType"
							placeholder="Select recipe type"
							value={formik.values.recipeType}
							options={recipeTypeSettings.map((setting) => ({
								label: setting.recipeType,
								value: setting.recipeType,
							}))}
							onChange={handleRecipeTypeChange}
							onBlur={formik.handleBlur}
							className={classNames('flex-1', {
								'p-invalid': formik.touched.recipeType && formik.errors.recipeType,
							})}
						/>
						<Button
							type="button"
							label="Apply defaults"
							icon="pi pi-refresh"
							className="w-auto p-button-outlined p-button-warning"
							disabled={!selectedRecipeTypeSettings}
							onClick={applyRecipeTypeDefaults}
						/>
					</div>
				) : (
					<div>Loading recipe types...</div>
				)}
				{formik.touched.recipeType && formik.errors.recipeType && (
					<small id="recipeType-help" className="p-error">
						<i className="pi pi-exclamation-triangle mr-1" />
						{formik.errors.recipeType}
					</small>
				)}
			</div>

			<div className="flex align-items-center gap-3 mt-3">
				<div className="flex-1">
					<div className="flex align-items-center gap-1">
						<label htmlFor="category">Category</label>
						<Button
							type="button"
							icon="pi pi-info-circle"
							text
							rounded
							size="small"
							className="w-auto"
							aria-label="Learn about recipe categories"
							onClick={() => setCategoryHelpVisible(true)}
						/>
					</div>
					{categories.length > 0 ? (
						<MultiSelect
							id="category"
							name="category"
							placeholder="Select a category"
							display="chip"
							value={formik.values.category}
							optionLabel="name"
							options={[
								...categories.map((category, index) => ({
									key: `${category + index}`,
									name: category.charAt(0) + category.slice(1).toLowerCase(),
									value: category,
								})),
							]}
							showSelectAll={false}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							className={classNames({ 'p-invalid': formik.touched.category && formik.errors.category })}
							panelHeaderTemplate={() => {
								const value = formik.values.category;
								const label = `${value.length === 0 ? 'Select a category' : `${value.length} selected`} selected`;
								return (
									<>
										{value.length > 0 && (
											<div className="border-round-top flex justify-content-between align-content-center pl-4 mt-2">
												<div className="flex justify-content-center align-items-center">{label}</div>

												<div>
													<Button
														icon="pi pi-times"
														className="p-button-text"
														onClick={() => {
															formik.setFieldValue('category', []);
														}}
													/>
												</div>
											</div>
										)}
									</>
								);
							}}
						/>
					) : (
						<div>No recipe type settings found</div>
					)}

					{formik.touched.category && formik.errors.category && (
						<small id="category-help" className="p-error">
							<i className="pi pi-exclamation-triangle mr-1" />
							{formik.errors.category}
						</small>
					)}
				</div>

				<div className="flex-1">
					<label htmlFor="waterRatio">Water ratio</label>
					<InputNumber
						id="waterRatio"
						inputId="waterRatio"
						name="waterRatio"
						mode="decimal"
						min={0}
						max={10}
						step={0.1}
						minFractionDigits={0}
						maxFractionDigits={6}
						useGrouping={false}
						value={formik.values.waterRatio}
						onValueChange={(e) => formik.setFieldValue('waterRatio', e.value)}
						onBlur={formik.handleBlur}
						className={classNames({
							'p-invalid': formik.touched.waterRatio && formik.errors.waterRatio,
						})}
					/>
					{formik.touched.waterRatio && formik.errors.waterRatio && (
						<small id="waterRatio-help" className="p-error">
							<i className="pi pi-exclamation-triangle mr-1" />
							{formik.errors.waterRatio}
						</small>
					)}
				</div>
			</div>

			<div className="flex flex-wrap gap-3 mt-3">
				<div className="flex-auto md:flex-1">
					<label htmlFor="weightRatio">Weight Ratio</label>
					<InputNumber
						id="weightRatio"
						inputId="weightRatio"
						name="weightRatio"
						mode="decimal"
						min={0}
						max={2}
						step={0.1}
						minFractionDigits={0}
						maxFractionDigits={6}
						useGrouping={false}
						value={formik.values.weightRatio}
						onValueChange={(e) => formik.setFieldValue('weightRatio', e.value)}
						onBlur={formik.handleBlur}
						className={classNames({
							'p-invalid': formik.touched.weightRatio && formik.errors.weightRatio,
						})}
					/>
					{formik.touched.weightRatio && formik.errors.weightRatio && (
						<small id="weightRatio-help" className="p-error">
							<i className="pi pi-exclamation-triangle mr-1" />
							{formik.errors.weightRatio}
						</small>
					)}
					{isWeightRatioZero && (
						<Message
							severity="info"
							text={`Weight ratio must be greater than 0 and cannot be submitted. Recipe-type defaults: ${
								weightRatioDefaults || 'No defaults are configured.'
							}`}
							className="w-full mt-2 justify-content-start"
						/>
					)}
				</div>
				<div className="flex-auto md:flex-1">
					<label htmlFor="steamConfiguration">Steam Configuration (ms)</label>
					<InputText
						id="steamConfiguration"
						name="steamConfiguration"
						value={formik.values.steamConfiguration}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						className={classNames({
							'p-invalid': formik.touched.steamConfiguration && formik.errors.steamConfiguration,
						})}
						type="number"
					/>

					{formik.touched.steamConfiguration && formik.errors.steamConfiguration && (
						<small id="steamConfiguration-help" className="p-error">
							<i className="pi pi-exclamation-triangle mr-1" />
							{formik.errors.steamConfiguration}
						</small>
					)}
				</div>
				<div className="flex-auto md:flex-1">
					<label htmlFor="blendConfiguration">Blend Configuration (ms)</label>
					<InputText
						id="blendConfiguration"
						name="blendConfiguration"
						value={formik.values.blendConfiguration}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						className={classNames({
							'p-invalid': formik.touched.blendConfiguration && formik.errors.blendConfiguration,
						})}
						type="number"
					/>

					{formik.touched.blendConfiguration && formik.errors.blendConfiguration && (
						<small id="blendConfiguration-help" className="p-error">
							<i className="pi pi-exclamation-triangle mr-1" />
							{formik.errors.blendConfiguration}
						</small>
					)}
				</div>
			</div>
		</>
	);
};

export default RecipeFormFields;
